/**
 * buddyMessages — message pools and selection logic for BuddyBubble.
 *
 * Pools are categorised by context: route, job status, time-of-day, and
 * a few mood variants (idle, teasing, click). All messages ≤ 50 chars.
 *
 * Selection algorithm:
 *   1. Resolve primary pool (job status → route → idle).
 *   2. 30% chance to pull from idle or teasing instead (variety).
 *   3. Once-per-hour: allow a timeMorning/timeNight message.
 *   4. Anti-repeat: skip if message appeared in last 3 swaps.
 */

export const BUDDY_POOLS = {
  intake: {
    start: [
      'on it — reading your request…',
      'getting up to speed on what you need',
      "right, let's get this down",
    ],
    gathering: [
      'making sure we capture this right',
      'a few questions and we are set',
      'pinning down what you need',
    ],
    drafting: [
      'drafting your requirement…',
      'writing this up properly',
      'turning your answers into a spec',
    ],
    checking: [
      'checking if this was already requested',
      'scanning for similar requests',
    ],
    saving: [
      'saving your request…',
      'filing it — almost done',
    ],
    done: [
      'got it — your request is captured ✓',
      'all set, filed and ready',
      "done — it's in good hands",
    ],
    failed: [
      'hit a snag — your answers are safe',
      "let's try that again, nothing lost",
    ],
  },
  idle: [
    'you see, but you do not observe',
    'every contact leaves a trace',
    'the game is afoot — eventually',
    'data is dangerous without theory',
    "I've solved harder before breakfast",
    'absence of evidence ≠ evidence of absence',
    'from a drop of water, an Atlantic',
    'the world is full of obvious things',
    "boredom is the detective's worst enemy",
    'no signs of mischief, watson',
    "I never guess. shocking habit.",
    'patience. the case will reveal itself.',
  ],
  routes: {
    '/insights': [
      'the lay of the land — read it carefully',
      "data tells stories. let's listen.",
      'a healthy system is a quiet one',
      'scanning vital signs',
      'trends rarely lie, watson',
      "the dog that didn't bark may matter",
      'metrics are evidence. read closely.',
    ],
    '/issues': [
      'the suspects are gathered',
      'every error has a witness',
      'stack traces are confessions',
      'the more outré the bug, the easier it solves',
      "errors don't lie. only stack traces do.",
      "let's pick a victim",
      'every bug is a story half-told',
    ],
    '/issues/*': [
      'examining the evidence closely',
      'this one has a story',
      'ruling out the obvious first',
      'look for the simpler explanation',
      'the simplest answer is usually correct',
      'every case is solvable, given enough tea',
    ],
    '/investigate': [
      'give me a window and a question',
      'the perfect crime needs a perfect detective',
      'ready when you are, watson',
      "I'll need three things: when, where, what",
      'ask, and the logs shall answer',
      "the evidence won't read itself",
    ],
    '/investigations': [
      'old cases, new wisdom',
      'every solved case teaches the next',
      'the archive — where I keep my receipts',
      'history rhymes, especially in distributed systems',
      'good investigations age well',
    ],
    '/investigations/*': [
      'a closed case still has lessons',
      "let's review the evidence",
      'rereading the crime scene',
      'the receipts, in chronological order',
      'the verdict, in detail',
    ],
    '/docs': [
      'so you want to know how I work?',
      'the case files — read at your leisure',
      "Watson left notes. they're decent.",
      'every detective leaves a manual',
      'pour the tea. settle in.',
      'a tour of the workshop',
      "I'll show you the magnifier later",
    ],
    '/docs/*': [
      'so you want to know how I work?',
      'the case files — read at your leisure',
      "Watson left notes. they're decent.",
      'every detective leaves a manual',
      'pour the tea. settle in.',
      'a tour of the workshop',
      "I'll show you the magnifier later",
    ],
  },
  running: [
    'still digging through the evidence',
    'checking three databases and a witness',
    'the trail is warm',
    'ruling out the impossible',
    'examining timelines',
    'cross-referencing logs',
    "this one's a four-pipe problem",
    'the chain of cause and effect is long',
    'follow the breadcrumbs',
    'interrogating the metrics',
    'the data is whispering — let me listen',
    'almost have it…',
    'every minute, more clues',
    'the suspect list is narrowing',
    // analysis-specific additions
    'still on the case, watson',
    'on it',
    'magnifier deployed',
    'scrutinising the evidence',
  ],
  done: [
    'elementary, watson',
    'case closed',
    'the curtain falls',
    'another mystery for the archive',
    'the suspect was the database, all along',
    'verdict: filed in the records',
    'another one for the books',
    'as suspected — hidden in plain sight',
    'the dog that barked was DNS',
    'you owe me a pint',
    'by deduction, not luck',
    'the impossible eliminated; truth remains',
    'case dismissed with prejudice',
    // re-analyze branch
    'second look, sharper eye',
    'reopening the file',
  ],
  cancelled: [
    'case dismissed',
    'stand down, watson',
    "filing it under 'later'",
  ],
  failed: [
    "the trail's gone cold ❄️",
    'even Holmes loses one occasionally',
    'I need more data, watson',
    'this one will haunt me',
    'the case is unresolved — for now',
    "we'll catch this one another day",
    'more questions than answers',
    'the evidence is contradictory',
  ],
  click: [
    'elementary',
    'you rang?',
    "I'm watching",
    'indeed',
    'go on',
    'fascinating',
    'carry on, watson',
    'the game continues',
    'pace yourself, watson',
    'do try not to make a mess',
    'I had it under control',
    'the suspense is killing me. it isn’t.',
    "my pipe was rather happy, you know",
    'one does not simply poke the detective',
    'careful, that tickles',
    'the magnifier is delicate, you know',
    'a poke for a poke. fair?',
    "I'm thinking. don't make me lose it.",
    'observed. duly noted.',
    'did you bring biscuits?',
    'mind the deerstalker',
    'elementary, my dear user.',
    'pardon the pipe.',
    'smoked you out.',
    'the game is very much afoot.',
    'kindly stop jostling the evidence.',
  ],
  teasing: [
    'have you tried turning it off and on?',
    'the cause is staring at you, watson',
    "this isn't your first rodeo, surely?",
    "I won't tell anyone if you don't",
    'even Holmes reads the docs sometimes',
    'we have solved harder. probably.',
    'the obvious answer is usually obvious',
    'may I suggest… checking the logs?',
  ],
  timeMorning: [
    'morning, watson 🌅',
    'the early detective catches the bug',
    'fresh eyes, fresh deductions',
    'tea first. then justice.',
    'a new day, a new case',
  ],
  timeNight: [
    'burning the midnight oil? 🌙',
    'the night is darkest before the case closes',
    'rest, watson. the case will keep.',
    'even Baker Street sleeps eventually',
    "tomorrow's clues are still warm",
  ],
} as const;

type RouteKey = keyof typeof BUDDY_POOLS.routes;

export type IntakePhase = 'start' | 'gathering' | 'drafting' | 'checking' | 'saving';

export type BuddyContext =
  | { kind: 'job'; status: 'running' | 'done' | 'failed' | 'cancelled'; identifier?: string }
  | {
      kind: 'intake';
      status: 'running' | 'paused' | 'done' | 'failed';
      phase: IntakePhase;
      coverage?: { covered: number; total: number };
    }
  | { kind: 'route'; path: string }
  | { kind: 'click' };

// Track when we last showed a time-of-day message (once per hour at most).
let lastTimeMsgHour = -1;

/**
 * Match a pathname against pool route keys (supports '*' wildcard for
 * detail pages like /issues/* and /investigations/*).
 */
function matchRoute(pathname: string): readonly string[] | null {
  const keys = Object.keys(BUDDY_POOLS.routes) as RouteKey[];

  // Try exact match first
  const exact = keys.find((k) => k === pathname);
  if (exact) return BUDDY_POOLS.routes[exact];

  // Try wildcard match: /foo/* matches /foo/anything
  const wildcard = keys.find((k) => {
    if (!k.endsWith('/*')) return false;
    const prefix = k.slice(0, -2); // strip /*
    return pathname.startsWith(prefix + '/');
  });
  if (wildcard) return BUDDY_POOLS.routes[wildcard];

  return null;
}

/**
 * Pick a random item from the pool that is NOT in lastMessages.
 * Falls back to any item if all messages were recently shown.
 */
function pickFrom(pool: readonly string[], lastMessages: string[]): string {
  const available = pool.filter((m) => !lastMessages.includes(m));
  const source = available.length > 0 ? available : [...pool];
  return source[Math.floor(Math.random() * source.length)] as string;
}

/**
 * Select a context-appropriate message from the buddy pools.
 *
 * @param context    The current rendering context (job status or route).
 * @param lastMessages  The last 3 messages shown (anti-repeat filter).
 */
export function pickMessage(context: BuddyContext, lastMessages: string[]): string {
  const now = new Date();
  const hour = now.getHours();

  // Once per hour, allow a time-of-day message.
  if (hour !== lastTimeMsgHour && (hour < 12 || hour >= 22)) {
    const timePool = hour < 12 ? BUDDY_POOLS.timeMorning : BUDDY_POOLS.timeNight;
    const timeMsg = pickFrom(timePool, lastMessages);
    if (timeMsg && !lastMessages.includes(timeMsg)) {
      lastTimeMsgHour = hour;
      return timeMsg;
    }
  }

  // Click context — always pulls from the click pool (witty/playful one-liners).
  if (context.kind === 'click') {
    return pickFrom(BUDDY_POOLS.click, lastMessages);
  }

  // Intake context — requirement-capture reassurance messages, phase-keyed.
  if (context.kind === 'intake') {
    if (context.status === 'done') {
      return pickFrom(BUDDY_POOLS.intake.done, lastMessages);
    }
    if (context.status === 'failed') {
      return pickFrom(BUDDY_POOLS.intake.failed, lastMessages);
    }
    // running or paused — pick from the phase pool
    const phasePool = BUDDY_POOLS.intake[context.phase] as readonly string[];
    const msg = pickFrom(phasePool, lastMessages);
    if (context.coverage && context.phase === 'gathering') {
      return `${msg} · ${context.coverage.covered}/${context.coverage.total}`;
    }
    return msg;
  }

  // Job-status context takes highest priority.
  if (context.kind === 'job') {
    const pool =
      context.status === 'running'
        ? BUDDY_POOLS.running
        : context.status === 'done'
          ? BUDDY_POOLS.done
          : context.status === 'cancelled'
            ? BUDDY_POOLS.cancelled
            : BUDDY_POOLS.failed;
    const msg = pickFrom(pool, lastMessages);
    // Append the identifier (e.g. investigation id, issue short hash) so the
    // user knows which job the buddy is referring to. Applied to all job
    // states (running shows progress; done/failed give closure).
    if (context.identifier) {
      return `${msg} · ${context.identifier}`;
    }
    return msg;
  }

  // Route-based context.
  const routePool = matchRoute(context.path);

  // 15% chance to use idle or teasing instead (variety, but the route pool
  // dominates so flavor lands on each page).
  const roll = Math.random();
  if (roll < 0.075) {
    return pickFrom(BUDDY_POOLS.idle, lastMessages);
  }
  if (roll < 0.15) {
    return pickFrom(BUDDY_POOLS.teasing, lastMessages);
  }

  if (routePool) {
    return pickFrom(routePool, lastMessages);
  }

  // Fallback to idle.
  return pickFrom(BUDDY_POOLS.idle, lastMessages);
}
